// src/utility/bridge/hybridBridge.js
// Web ↔ Native secure bridge (website side)

import { verifyEnvelope } from "./verifyEnvelope";
import {
    ALLOWLIST_NATIVE_TO_WEB,
    ALLOWLIST_WEB_TO_NATIVE,
} from "./constants";

let runtimeKey = null; // in-memory only: { keyId, secret, expiresAt? }

/**
 * Called right after user login.
 * Backend should return a short-lived HMAC key: { keyId, secret, expiresAt? }.
 */
export function setBridgeKey(key) {
    runtimeKey = key;
}

/**
 * Optional helper if you ever need to clear the key (logout, etc.)
 */
export function clearBridgeKey() {
    runtimeKey = null;
}

/**
 * Send secure message to native app.
 * Example: sendToNative("AUTH_EVENT", { isLoggedIn: true })
 */
export async function sendToNative(type, payload) {
    if (!runtimeKey) {
        console.warn("[Bridge] No runtime key set; ignoring sendToNative");
        return;
    }

    if (!ALLOWLIST_WEB_TO_NATIVE.has(type)) {
        console.warn("[Bridge] Type not allowed for Web→Native:", type);
        return;
    }

    if (runtimeKey.expiresAt && Date.now() > runtimeKey.expiresAt) {
        console.warn("[Bridge] Runtime key expired; ignoring sendToNative");
        return;
    }

    try {
        const envelope = await buildEnvelope(type, payload);
        window.ReactNativeWebView?.postMessage(JSON.stringify(envelope));
    } catch (err) {
        console.error("[Bridge] Failed to send envelope to native:", err);
    }
}

/**
 * Build a signed HMAC envelope
 */
async function buildEnvelope(type, payload) {
    const base = {
        v: "1.0",
        id: crypto.randomUUID(),
        type,
        payload,
        ts: Date.now(),
        nonce: randomNonce(),
        kid: runtimeKey.keyId,
    };

    const toSign = canonicalStringify(base);
    const sig = await hmacBase64Url(toSign, runtimeKey.secret);

    return { ...base, sig };
}

/**
 * Verify envelope received from Native deep-link route
 * (Native → Web)
 */
export async function verifyIncomingEnvelope(envelope) {
    if (!runtimeKey) {
        console.warn("[Bridge] No runtime key when verifying incoming envelope");
        return false;
    }

    if (!envelope || typeof envelope !== "object") return false;

    if (!ALLOWLIST_NATIVE_TO_WEB.has(envelope.type)) {
        console.warn("[Bridge] Native→Web type not allowed:", envelope.type);
        return false;
    }

    return await verifyEnvelope(envelope, runtimeKey.secret, runtimeKey.keyId);
}

/* ------------------------------------------------------------------
 * Convenience message helpers (Web → Native)
 * ----------------------------------------------------------------- */

export function sendUserLoggedOut() {
    return sendToNative("AUTH_EVENT", { isLoggedIn: false });
}

export async function sendUserLoggedIn(accessToken, refreshToken) {
    try {
        const payload = {
            isLoggedIn: true,
            accessToken,
            refreshToken,
        };

        await sendToNative("AUTH_EVENT", payload);
        console.log("[Bridge] AUTH_EVENT sent to native (login)");
    } catch (err) {
        console.error("[Bridge] Failed to send AUTH_EVENT:", err);
    }
}

export function sendNavigation(path, isGuest) {
    return sendToNative("NAVIGATE", { path, isGuest });
}

export async function sendUtilityReceipt(base64, fileName) {
    try {
        const payload = { base64, fileName };

        await sendToNative("UTILITY_RECEIPT", payload);
        console.log("[Bridge] UTILITY_RECEIPT sent to native");
    } catch (err) {
        console.error("[Bridge] Failed to send UTILITY_RECEIPT:", err);
    }
}

export async function sendHybridPayment(token) {
    try {
        const payload = { token };

        await sendToNative("HYBRID_PAYMENT", payload);
        
        console.log("[Bridge] HYBRID_PAYMENT sent to native");
    } catch (err) {
        console.error("[Bridge] Failed to send HYBRID_PAYMENT:", err);
    }
}

/**
 * Ask native app to open external URL
 * Usage example in Checkout page:
 *   sendOpenExternalUrl(paymentRedirectUrl);
 */
export function sendOpenExternalUrl(url) {
    if (!url) {
        console.warn("[Bridge] sendOpenExternalUrl called with empty url");
        return;
    }
    return sendToNative("OPEN_EXTERNAL_URL", { url });
}

/* ------------------------------------------------------------------
 * Helpers
 * ----------------------------------------------------------------- */

function canonicalStringify(obj) {
    return JSON.stringify(
        Object.keys(obj)
            .sort()
            .reduce((acc, key) => {
                acc[key] = obj[key];
                return acc;
            }, {})
    );
}

async function hmacBase64Url(data, secret) {
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey(
        "raw",
        enc.encode(secret),
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"]
    );
    const sig = await crypto.subtle.sign("HMAC", key, enc.encode(data));
    const b64 = btoa(String.fromCharCode(...new Uint8Array(sig)));

    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function randomNonce() {
    return crypto
        .getRandomValues(new Uint8Array(16))
        .reduce((s, b) => s + ("0" + b.toString(16)).slice(-2), "");
}
